import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useRef, useState } from "react";
import { useFormikContext } from "formik";
import AddButton from "../../buttons/button";
import CloseIcon from "../../../assets/icons/close-icon";
import { productViewAction } from "../../../redux/actions/product-action";
import { useDispatch } from "react-redux";
import LoadingOverlay from "../../helper-components/overlay-loading";
import IndianRupeeIcon from "../../../assets/icons/indian-rupee";
import ItemsInputField from "../../forms/items-input-field";
import errorToast from "../../helper-components/error-toast";
import { useHotkeys } from "../../helper-components/use-hotkeys";
import React from "react";
import SelectField from "../../forms/select-field";
import { glAccListAction } from "../../../redux/actions/sales-action";
import LoadingSpinner from "../../helper-components/loading-spinner";

export default function PurchasePaymentPanel({
  clearDue,
  page,
  checkedId,
  section,
  submitLoading,
  open,
  setOpen,
  selectedItem,
  payModesList,
  disabled = false,
}) {
  const addRef = useRef(null);
  const closeRef = useRef(null);
  const submitRef = useRef(null);
  const buttonRefs = useRef([]);

  const prefix = section === "pit" ? "soit" : "poit";

  if (buttonRefs.current.length !== payModesList?.length) {
    buttonRefs.current = payModesList.map(
      (_, i) => buttonRefs.current[i] || React.createRef()
    );
  }

  useHotkeys("a", () => {
    if (addRef.current) {
      addRef.current.click();
    }
  });
  useHotkeys("x", () => {
    if (closeRef.current) {
      closeRef.current.click();
    }
  });
  useHotkeys("1,2,3,4,5,6,7,8,9", (event) => {
    const index = parseInt(event.key, 10) - 1;
    const button = buttonRefs.current[index];
    button?.current?.click();
  });
  useHotkeys("s", () => {
    if (submitRef.current) {
      submitRef.current.click();
    }
  });

  const { setFieldValue, values, submitForm, actions, validateForm } =
    useFormikContext();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [productDetails, setProductDetails] = useState({
    item_id: selectedItem?.itemid,
  });
  const [gllist, setGllist] = useState([]);
  const [currentPaymentMethod, setcurrentPaymentMethod] = useState({
    method: "",
    index: "",
  });

  const inputClass =
    "min-h-[40px] px-2 border border-[var(--pay-met-input-border)] focus:outline-none bg-[var(--pay-met-input-bg)] text-[var(--pay-met-val-color)] text-[length:var(--pay-met-val-fs)] font-[var(--pay-met-val-fw)] border-radius input-field-no-spin-buttons";

  const labelClass =
    "min-h-[1.2rem] text-[var(--pay-met-lab-color)] text-[length:var(--pay-met-lab-fs)] font-[var(--pay-met-lab-fw)]";

  // Helper function to fix decimal precision
  const fixPrecision = (num) => Math.round(num * 100) / 100;

  // Calculate remaining amount dynamically
  const totalPayment =
    values?.paymentModeData?.reduce((acc, curr) => {
      return acc + parseFloat(curr?.pmt_total_amount || 0);
    }, 0) || 0;

  // In clear-due mode the payment targets the open balance, but once invoices
  // are ticked it must target their total, same as the footer's current payment
  const isClearingDueOnly = clearDue && !(checkedId?.length > 0);
  const currentPaymentAmount = parseFloat(
    isClearingDueOnly ? values?.clearDueInput : values?.fixedCurrentPayment
  );

  const remaining_amount = fixPrecision(currentPaymentAmount - totalPayment);

  useEffect(() => {
    const onSuccess = (data) => {
      setGllist(
        data?.data.map((item) => {
          return {
            label: item?.account_name,
            value: item?.account_id,
            account_code: item?.account_code,
          };
        })
      );
    };
    const onError = (data) => {};
    dispatch(glAccListAction(onSuccess, onError));
  }, []);
  // useEffect(() => {
  //   if (clearDue) setFieldValue("fixedCurrentPayment", values.clearDueInput);
  // }, [clearDue, values.clearDueInput]);
  useEffect(() => {
    setProductDetails((prev) => ({
      ...prev,
      item_id: selectedItem?.itemid ?? "",
    }));
  }, [selectedItem]);

  useEffect(() => {
    if (productDetails.item_id) {
      setLoading(true);
      dispatch(productViewAction(productDetails.item_id, onSuccess, onError));
    }
  }, [productDetails.item_id, open]);

  const onSuccess = (data) => {
    setLoading(false);
    setProductDetails(data?.data?.[0]);
  };

  const onError = () => {
    setLoading(false);
  };

  const handleAmountChange = (index, inputValue) => {
    setFieldValue(`paymentModeData[${index}].pmt_total_amount`, inputValue);

    // Build a new list instead of writing into values.paymentModeData: those
    // objects are shared with Formik's initialValues, which the invoice table
    // and vendor change reset the payment modes back to
    const updatedPaymentModeData = (values?.paymentModeData || []).map(
      (item, i) => (i === index ? { ...item, pmt_total_amount: inputValue } : item)
    );

    const difference =
      amtpaid(updatedPaymentModeData) -
      (values?.sumOfAmountEnteredInAllInvoices +
        parseFloat(values?.credit_note_amount) +
        parseFloat(values?.journal_entry));
    // -
    // parseFloat(values?.[`${section}_open_balance`])
    // Only an overpayment goes on account; a shortfall stays as balance due
    // on the invoices, so it must not be counted here as a negative amount
    if (amtpaid(values?.invoices) > 0) {
      setFieldValue(
        `${section}_add_to_account_amount`,
        Math.max(0, difference)
      );
    } else {
      if (page !== "view") {
        setFieldValue(`${section}_add_to_account_amount`, 0);
      }
    }
    setFieldValue(
      `${section}_cmr_amount_paid`,
      amtpaid(updatedPaymentModeData)
    );
    setFieldValue(
      `${section}_current_payment`,
      amtpaid(updatedPaymentModeData)
    );
  };

  const handlePaymentMethodChange = (type, index) => {
    if (
      currentPaymentMethod.index !== "" &&
      currentPaymentMethod.index !== index
    ) {
      const prevIndex = currentPaymentMethod.index;
    }

    // Set new payment method
    setcurrentPaymentMethod({
      method: type?.pmt_mode_name,
      index: index,
    });

    // Auto-fill remaining amount if current amount is 0, going through
    // handleAmountChange so the on-account and paid totals are recalculated
    if (
      !disabled &&
      parseFloat(values.paymentModeData[index].pmt_total_amount || 0) === 0 &&
      remaining_amount > 0
    ) {
      handleAmountChange(index, remaining_amount);
    }
  };

  function amtpaid(updatedPaymentModeData) {
    if (
      updatedPaymentModeData.reduce(
        (acc, item) => acc + parseFloat(item?.pmt_total_amount),
        0
      ) > 0
    ) {
      return updatedPaymentModeData.reduce(
        (acc, item) => acc + parseFloat(item?.pmt_total_amount),
        0
      );
    } else {
      return (
        values?.invoices
          .filter(
            (item) =>
              checkedId?.includes(item?.id) &&
              item[`${prefix}_total_payment`] > 0
          )
          .reduce((acc, curr) => {
            const value = isNaN(curr?.[`${prefix}_total_payment`])
              ? 0
              : acc + parseFloat(curr?.[`${prefix}_total_payment`]);

            return acc + value;
          }, 0) -
        parseFloat(values?.[`${section}_open_balance`]) +
        parseFloat(values?.credit_note_amount)
      );
    }
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => {
          setOpen(false);
        }}
      >
        <div className="fixed inset-0 overflow-hidden">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="absolute inset-0 bg-[#0000004D]" />
          </Transition.Child>

          <div className="fixed inset-0 flex justify-end">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-300"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="h-full relative w-[400px] sm:w-[600px] bg-white overflow-y-auto rounded-tl-lg rounded-bl-lg shadow-xl">
                {/* Header */}
                <div
                  className="h-[61px] bg-[var(--slide-panel-head-bg)] px-3 flex items-center justify-between border-b"
                  style={{ borderColor: "var(--slide-panel-border)" }}
                >
                  <div
                    style={{
                      maxHeight: "32px",
                      color: "var(--slide-panel-h-fg)",
                      fontSize: "var(--slide-panel-h-fs)",
                      fontWeight: "var(--slide-panel-h-fw)",
                    }}
                    className="min-w-[75%] "
                  >
                    Payment
                  </div>
                  <AddButton
                    ref={closeRef}
                    onclkFn={() => {
                      setOpen(false);
                    }}
                    icon={
                      <CloseIcon
                        width="12px"
                        height="12px"
                        fill="var(--close-button-fg)"
                      />
                    }
                    text={
                      <span>
                        Close <span className="opacity-50">(X)</span>{" "}
                      </span>
                    }
                    className="flex h-[32px]  items-center gap-2 bg-[var(--close-button-bg)] border-radius px-3 text-[var(--close-button-fg)]"
                  />
                </div>

                {/* Product Detail Panel */}
                <div
                  style={{
                    height: `calc(100% - 61px)`,
                    minHeight: `calc(100% - 61px)`,
                  }}
                  className="px-3 relative"
                >
                  <div className="h-[5.12rem] w-full flex items-center">
                    <div className="bg-[var(--net-payable-con-bg)] min-h-[52px] flex items-center px-4 justify-between rounded-[8px] w-full">
                      <span className="text-[var(--net-payable-text-color)] text-[length:var(--net-payable-fs)] font-[var(--net-payable-label-fw)]">
                        Current Payment Amount
                      </span>
                      {console.log(
                        values.clearDueInput,
                        clearDue,
                        parseFloat(values?.clearDueInput).toFixed(2)
                      )}
                      {console.error(
                        "fixedCurrentPayment",
                        parseFloat(values?.fixedCurrentPayment).toFixed(2)
                      )}

                      <span className="text-[var(--net-payable-text-color)] text-[length:var(--net-payable-fs)] font-[var(--net-payable-value-fw)]">
                        ₹ {currentPaymentAmount.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <div className="border border-[var(--slide-panel-border)] rounded ">
                    <div className="h-full overflow-auto">
                      <div className="flex flex-wrap gap-4 m-4">
                        {values?.paymentModeData?.map((type, index) => (
                          <button
                            ref={buttonRefs.current[index]}
                            key={index}
                            onClick={() => {
                              handlePaymentMethodChange(type, index);
                            }}
                            className={`
            ${
              currentPaymentMethod.method === type?.pmt_mode_name
                ? "text-white bg-[var(--pay-type-selec-bg)]"
                : "text-[var(--pay-type-con-fg)] bg-[var(--pay-type-con-bg)]"
            }
            flex items-center justify-between  p-3 h-[44px]
             rounded
            text-[length:var(--pay-type-con-fs)] font-[var(--pay-type-con-fw)]
            flex-grow-0 self-start whitespace-nowrap
          `}
                          >
                            <span className="flex items-center gap-2">
                              <IndianRupeeIcon
                                fill={
                                  currentPaymentMethod.method ===
                                  type?.pmt_mode_name
                                    ? "white"
                                    : "black"
                                }
                              />
                              <span>{type?.pmt_mode_name}</span>
                            </span>
                            <span className="ml-[10px]">({index + 1})</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="border border-[var(--slide-panel-border)]  border-radius mt-[1rem] h-max">
                    <div className="bg-[var(--payment-summary-head-bg)] text-[var(--payment-summary-head-fg)] text-[length:var(--payment-summary-head-fs)] font-[var(--payment-summary-head-fw)] w-full h-[52px] flex items-center px-4 border-radius">
                      Payment Summary
                    </div>
                    <div className="flex flex-col gap-3 p-4 ">
                      {currentPaymentMethod.method?.length > 0 && (
                        <>
                          <div className=" ">
                            <div className="flex gap-3 w-full">
                              <div
                                className={`${
                                  currentPaymentMethod.method == "Card" ||
                                  currentPaymentMethod.method == "Cheque"
                                    ? "flex-[2]"
                                    : "flex-[6]"
                                } flex flex-col justify-between h-[59.2px]`}
                              >
                                <label className="text-[var(--pay-met-lab-color)] text-[length:var(--pay-met-lab-fs)] font-[var(--pay-met-lab-fw)]">
                                  Account Effect
                                </label>
                                <SelectField
                                  general_color="true"
                                  defaultSelectClass={labelClass}
                                  borderclass="border border-[var(--pay-met-input-border)] rounded-lg"
                                  containerClass={labelClass}
                                  labelClass={` m-0 p-0 ${labelClass}`}
                                  isDisabled={disabled}
                                  errMsgContainerClass="hidden"
                                  handleSelected={(selected) => {
                                    setFieldValue(
                                      `paymentModeData[${currentPaymentMethod.index}].account_name`,
                                      selected?.label
                                    );
                                    setFieldValue(
                                      `paymentModeData[${currentPaymentMethod.index}].account_code`,
                                      selected?.account_code
                                    );
                                  }}
                                  defaultValue={{
                                    label:
                                      values.paymentModeData[
                                        currentPaymentMethod.index
                                      ]?.account_name,
                                  }}
                                  placeholder="Select account"
                                  options={gllist}
                                  id={`paymentModeData[${currentPaymentMethod.index}].account_id`}
                                  name={`paymentModeData[${currentPaymentMethod.index}].account_id`}
                                  isSearchable={true}
                                  isRequired={false}
                                />
                              </div>
                              <div
                                className={
                                  currentPaymentMethod.method == "Card" ||
                                  currentPaymentMethod.method == "Cheque"
                                    ? "flex-[1]"
                                    : "flex-[5]"
                                }
                              >
                                <ItemsInputField
                                  inputClass={inputClass}
                                  labelClass={labelClass}
                                  disabled={disabled}
                                  type="date"
                                  name={`paymentModeData[${currentPaymentMethod.index}].date_of_transfer`}
                                  label="Date of Transfer"
                                  isLabelReq="true"
                                />
                              </div>
                              <div
                                className={
                                  currentPaymentMethod.method == "Card" ||
                                  currentPaymentMethod.method == "Cheque"
                                    ? "flex-[1]"
                                    : "flex-[8]"
                                }
                              >
                                {(currentPaymentMethod.method === "Card" ||
                                  currentPaymentMethod.method === "Cheque") && (
                                  <div className="flex-[5]">
                                    <ItemsInputField
                                      inputClass={inputClass}
                                      labelClass={labelClass}
                                      disabled={disabled}
                                      type="date"
                                      name={`paymentModeData[${
                                        currentPaymentMethod.index
                                      }].${
                                        currentPaymentMethod.method === "Card"
                                          ? "expiry_date"
                                          : "date_of_cheque"
                                      }`}
                                      label={
                                        currentPaymentMethod.method === "Card"
                                          ? "Expiry Date"
                                          : "Date of Cheque"
                                      }
                                      isLabelReq="true"
                                    />
                                  </div>
                                )}
                                {(currentPaymentMethod.method ===
                                  "Bank Transfer" ||
                                  currentPaymentMethod.method === "UPI") && (
                                  <ItemsInputField
                                    className
                                    disabled={disabled}
                                    name={`paymentModeData[${
                                      currentPaymentMethod.index
                                    }].${
                                      currentPaymentMethod.method ===
                                      "Bank Transfer"
                                        ? "bank_reference_number"
                                        : "upi_reference_number"
                                    }`}
                                    label={`${
                                      currentPaymentMethod.method ===
                                      "Bank Transfer"
                                        ? "Bank"
                                        : "UPI"
                                    } Referance Number`}
                                    isLabelReq={true}
                                    labelClass={labelClass}
                                    inputClass={
                                      "min-h-[40px] px-2 border border-[var(--pay-met-input-border)] focus:outline-none bg-[var(--pay-met-input-bg)] text-[var(--pay-met-val-color)] text-[length:var(--pay-met-val-fs)] font-[var(--pay-met-val-fw)] border-radius"
                                    }
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                          {currentPaymentMethod?.method !== "Bank Transfer" &&
                            currentPaymentMethod?.method !== "Cash" && (
                              <ItemsInputField
                                disabled={disabled}
                                type="number"
                                name={(() => {
                                  const field = () => {
                                    if (currentPaymentMethod.method == "UPI")
                                      return "pmt_id";
                                    else if (
                                      currentPaymentMethod.method == "Card"
                                    )
                                      return "card_number";
                                    else if (
                                      currentPaymentMethod.method == "Cheque"
                                    )
                                      return "cheque_number";
                                  };
                                  return `paymentModeData[${
                                    currentPaymentMethod.index
                                  }][${field()}]`;
                                })()}
                                label={(() => {
                                  if (currentPaymentMethod.method == "UPI")
                                    return "Payment ID";
                                  else if (
                                    currentPaymentMethod.method == "Card"
                                  )
                                    return "Card Number";
                                  else if (
                                    currentPaymentMethod.method == "Cheque"
                                  )
                                    return "Cheque Number";
                                })()}
                                isLabelReq={true}
                                labelClass={labelClass}
                                inputClass={inputClass}
                              />
                            )}
                          <ItemsInputField
                            type="number"
                            name={`paymentModeData[${currentPaymentMethod.index}].pmt_total_amount`}
                            label="Enter Amount"
                            disabled={disabled}
                            isLabelReq={true}
                            labelClass={labelClass}
                            onChange={(e) => {
                              let correctedValue =
                                e.target.value < 0 ? 0 : e.target.value;

                              handleAmountChange(
                                currentPaymentMethod.index,
                                correctedValue
                              );
                            }}
                            onBlur={(e) => {
                              let inputValue = e.target.value;

                              if (
                                inputValue === "" ||
                                inputValue === null ||
                                inputValue === undefined ||
                                isNaN(inputValue)
                              ) {
                                handleAmountChange(
                                  currentPaymentMethod.index,
                                  0
                                );
                              }
                            }}
                            inputClass={inputClass}
                          />
                        </>
                      )}

                      <div className="w-full  border-[var(--payment-summery-lable-bg)] border-[1px] rounded-lg p-4">
                        <div className="flex flex-col space-y-4">
                          <div className="flex justify-between items-center">
                            <span className={labelClass}>Total Amount</span>
                            <span className="text-black text-[length:var(--pay-met-lab-fs)] font-[700]">
                              ₹ {totalPayment}
                            </span>
                          </div>

                          <div className="border-[var(--payment-summery-lable-bg)] border-t-[1px] "></div>
                          <div className="flex justify-between items-center">
                            <span className={labelClass}>Remaining Amount</span>
                            <span className="text-black text-[length:var(--pay-met-lab-fs)] font-[700]">
                              ₹ {remaining_amount.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <LoadingOverlay isLoading={loading} />
                </div>

                <LoadingOverlay isLoading={submitLoading} />
                <div
                  className={`h-[5rem] absolute bottom-0 flex justify-end items-center px-3 w-full`}
                >
                  <AddButton
                    ref={submitRef}
                    isDisabled={disabled}
                    type="submit"
                    onclkFn={
                      !submitLoading &&
                      (async () => {
                        setFieldValue("submittedButton", "save");

                        const errors = await validateForm();
                        if (totalPayment === 0) {
                          errorToast("Please enter amount.");
                        } else {
                          if (Object.keys(errors).length === 0) {
                            if (
                              values?.[`${section}_add_to_account_amount`] > 0
                            ) {
                              const isConfirmed = window.confirm(
                                "You are paying extra amount. Are you sure you want to submit this payment?"
                              );
                              if (!isConfirmed) {
                                return; // User cancelled, don't proceed
                              } else if (isConfirmed) submitForm();
                            } else submitForm();
                          } else {
                            // There are validation errors, display them
                            errorToast("Please fill all the details.");
                          }
                        }
                      })
                    }
                    prefixIcon={false}
                    text={
                      <span>
                        Save <span className="opacity-50">(S)</span>
                      </span>
                    }
                    className="
      flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px]
      bg-[var(--primary-button-bg-color)] cursor-pointer
      text-[var(--primary-button-fg-color)]
      border border-[var(--primary-button-bg-color)]
      hover:bg-[var(--primary-button-hover-color)]
      text-[length:var(--primary-button-font-size)]
      font-[var(--primary-button-font-weight)]
    "
                  />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
